import { PostAdd, PublishedWithChanges } from '@mui/icons-material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import moment from 'moment';
import { memo, useMemo } from 'react';
import ExportButton from "../../commons/export/ExportButton";
import { tableConfig } from "../../commons/table/tableConfig";
import { StyledGestionDeObra } from '../../styled/Obras';
import GestionDeObraTableDetailsPanel from './GestionDeObraTableDetailsPanel';

const GestionDeObraTable = ({
  data,
  loading,
  onVerObra,
  onVerFormularioCargado,
  onOpenFinalizarObra,
  onVerCertificadoDePresentacion,
  onGestionarObra,
  onOpenProgramaSeguridad,
  onOpenVerProgramaSeguridad,
}) => {

  const columns = useMemo(() => [
    {
      accessorKey: "nroObraSRT",
      header: "Nro. de Obra",
      size: 110,
      minSize: 80,
      sortingFn: (rowA, rowB) => parseInt(rowA.original.nroObraSRT) - parseInt(rowB.original.nroObraSRT),
    },
    {
      accessorKey: "tipoObra",
      header: "Decreto"
    },
    {
      accessorKey: "fechaInicio",
      header: "Fecha Inicio",
      size: 115,
      minSize: 95,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        return value ? moment(value).format("DD/MM/YYYY") : "";
      },
      filterFn: (row, id, filterValue) => {
        return moment(row.original.fechaInicio).format("DD/MM/YYYY").includes(filterValue);
      },
    },
    {
      accessorKey: "fechaFin",
      header: "Fecha Fin",
      size: 115,
      minSize: 95,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        return value ? moment(value).format("DD/MM/YYYY") : "";
      },
      filterFn: (row, id, filterValue) => {
        return moment(row.original.fechaFin).format("DD/MM/YYYY").includes(filterValue);
      },
    },
    {
      accessorKey: "denominacion",
      header: "Denominación",
      size: 180,
      minSize: 100,
      muiTableBodyCellProps: {
        sx: {
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        },
      },
    },
    {
      accessorKey: "establecimiento",
      header: "Establecimiento / Lugar",
      size: 180,
      minSize: 100,
      muiTableBodyCellProps: {
        sx: {
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        },
      },
    },
    {
      accessorKey: "estado",
      header: "Estado",
      size: 120,
      minSize: 80,
    },
    {
      accessorKey: "programaSeguridad",
      header: "Tiene Programa de Seguridad",
      size: 100,
      minSize: 80,
      filterVariant: 'select',
      filterSelectOptions: [
        { value: "true", text: 'Si' },
        { value: "false", text: 'No' },
      ],
      filterFn: (row, id, filterValue) => {
        if (!filterValue && filterValue !== false) return true;
        const boolValue = filterValue === "true";
        return row.getValue(id) === boolValue;
      },
      Cell: ({ cell }) => (cell.getValue() ? "Si" : "No"),
    },
  ], []);

  const table = useMaterialReactTable({
    ...tableConfig,
    columns,
    data,
    muiTableContainerProps: {
      ...tableConfig.muiTableContainerProps,
      sx: {
        ...tableConfig.muiTableContainerProps?.sx,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflowX: 'auto',
      },
    },
    muiTablePaperProps: {
      sx: {
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
      },
    },
    muiTableProps: {
      sx: {
        width: 'max-content',
        minWidth: '100%',
        tableLayout: 'fixed',
      },
    },
    state: {
      ...tableConfig.state,
      isLoading: loading,
      showGlobalFilter: false,
    },
    initialState: {
      ...tableConfig.initialState,
      pagination: {
        pageSize: 10,
        pageIndex: 0,
      },
      density: 'compact',
    },
    positionActionsColumn: 'last',
    muiTableBodyRowProps: { hover: true },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: 'Acciones',
        size: 220,
        minSize: 200,
        muiTableHeadCellProps: {
          ...tableConfig.muiTableHeadCellProps,
          sx: {
            ...tableConfig.muiTableHeadCellProps.sx,
            justifyContent: 'start',
          }
        }
      },
      'mrt-row-expand': {
        size: 50,
        minSize: 44,
      },
    },
    enableRowActions: true,
    muiDetailPanelProps: {
      sx: {
        width: '100%',
        '& .MuiCollapse-root': {
          width: '100%',
          minWidth: 0,
        },
      }
    },
    renderDetailPanel: ({ row }) => (
      <StyledGestionDeObra.DetailPanel>
        <GestionDeObraTableDetailsPanel
          data={row.original.avisosObra}
          onVerFormularioCargado={onVerFormularioCargado}
          onVerCertificadoDePresentacion={onVerCertificadoDePresentacion}
        />
      </StyledGestionDeObra.DetailPanel>

    ),
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: '0.5rem' }}>
        <Tooltip title="Ver formulario">
          <IconButton onClick={() => onVerObra(row.original)}>
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Gestionar Obra">
          <span>
            <IconButton
              disabled={row.original.estado === 'Finalizado'}
              onClick={() => onGestionarObra(row.original)}
            >
              {
                row.original.estado === 'Finalizado' ?
                  <StyledGestionDeObra.DisabledIcon component={PostAdd} />
                  :
                  <StyledGestionDeObra.GestionObraIcon />
              }
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Adjuntar Programa de Seguridad">
          <IconButton onClick={() => onOpenProgramaSeguridad(row.original)}>
            <StyledGestionDeObra.ProgramaSeguridadIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Ver Programa de Seguridad">
          <IconButton
            disabled={!row.original.programaSeguridad}
            onClick={() => onOpenVerProgramaSeguridad(row.original)}
          >
            <FileDownloadOutlinedIcon sx={{ color: !row.original.programaSeguridad ? "grey" : "red" }} />
          </IconButton>
        </Tooltip>
        {CONFIG.habilitarFinalizarObra &&
          <Tooltip title="Finalizar Obra">
            <span>
              <IconButton
                disabled={row.original.estado === 'Finalizado'}
                onClick={() => onOpenFinalizarObra(row.original)}
              >
                {
                  row.original.estado === 'Finalizado' ?
                    <StyledGestionDeObra.DisabledIcon component={PublishedWithChanges} />
                    :
                    <StyledGestionDeObra.FinalizarObraIcon />
                }
              </IconButton>
            </span>
          </Tooltip>}
      </Box>
    ),
    // Custom top toolbar with legend
    renderTopToolbar: ({ table }) => (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'right', p: '8px' }}>
          <ExportButton
            data={data.map((d) => ({
              ...d,
              fechaInicio: d.fechaInicio ? moment(d.fechaInicio).format("DD/MM/YYYY") : "",
              fechaFin: d.fechaFin ? moment(d.fechaFin).format("DD/MM/YYYY") : "",
              programaSeguridad: d.programaSeguridad ? "Si" : "No",
            }))}
            columns={columns.map(col => ({
              accessorKey: col.accessorKey,
              header: col.header
            }))}
            disabled={!data || data.length === 0}
            title="Gestión de Obras"
          />
        </Box>

        <Grid container direction="column" style={{ maxWidth: "fit-content", padding: '8px' }}>
          <Grid container item spacing={1} xs={12} nowrap="true">
            <StyledGestionDeObra.GridRowItem item xs={3}>
              <StyledGestionDeObra.GridItemAligned component={VisibilityIcon} />
              <Typography>
                Formulario de la Obra Actual
              </Typography>
            </StyledGestionDeObra.GridRowItem>
            <StyledGestionDeObra.GridRowItem item xs={3}>
              <StyledGestionDeObra.GridItemAligned component={StyledGestionDeObra.GestionObraIcon} />
              <Typography>
                Gestionar Obra (Edición, Extensión, Suspensión, Reinicio)
              </Typography>
            </StyledGestionDeObra.GridRowItem>
            <StyledGestionDeObra.GridRowItem item xs={3}>
              <StyledGestionDeObra.GridItemAligned component={StyledGestionDeObra.ProgramaSeguridadIcon} />
              <Typography>
                Adjuntar Programa de Seguridad
              </Typography>
            </StyledGestionDeObra.GridRowItem>
            <StyledGestionDeObra.GridRowItem item xs={3}>
              <StyledGestionDeObra.GridItemAligned sx={{ color: "red" }} component={FileDownloadOutlinedIcon} />
              <Typography>
                Ver Programa de Seguridad
              </Typography>
            </StyledGestionDeObra.GridRowItem>
          </Grid>

          <Grid container item spacing={1} xs={12} nowrap="true">
            <StyledGestionDeObra.GridRowItem item xs={3}>
              <StyledGestionDeObra.GridItemAligned component={StyledGestionDeObra.FormularioCargadoIcon} />
              <Typography>
                Ver Formulario Cargado
              </Typography>
            </StyledGestionDeObra.GridRowItem>
            <StyledGestionDeObra.GridRowItem item xs={3}>
              <StyledGestionDeObra.GridItemAligned component={StyledGestionDeObra.CertificadoPresentacionIcon} />
              <Typography>
                Certificado de Presentación
              </Typography>
            </StyledGestionDeObra.GridRowItem>
            <StyledGestionDeObra.GridRowItem item xs={3}>
              <StyledGestionDeObra.GridItemAligned component={StyledGestionDeObra.FinalizarObraIcon} />
              <Typography>
                Finalizar Obra
              </Typography>
            </StyledGestionDeObra.GridRowItem>
          </Grid>
        </Grid>
      </Box>
    ),
    muiExpandButtonProps: { sx: { alignSelf: 'center' } },
  });

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
      <MaterialReactTable table={table} />
    </Box>
  )
}

export default memo(GestionDeObraTable);